import { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, Grid, Button, Stack, Chip, Skeleton, Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import { b2bColors } from '../styles/b2bTheme';
import { formatPrice, formatDate, orderStatusLabel } from '../utils/format';
import { getErrorMessage, buildErrorSnackbarOptions } from '../utils/errorUtils';

export default function Dashboard() {
    const { user } = useAuth();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [orders, setOrders] = useState(null);
    const [feed, setFeed] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [ordersRes, feedRes] = await Promise.all([
                    api.get('/b2b/orders', { params: { limit: 5 } }),
                    api.get('/b2b/promo-feed'),
                ]);
                if (cancelled) return;
                setOrders(ordersRes.data.orders || []);
                setFeed(feedRes.data.items || []);
            } catch (err) {
                if (cancelled) return;
                enqueueSnackbar(getErrorMessage(err, 'Chargement impossible'), buildErrorSnackbarOptions(err, closeSnackbar));
                setOrders([]);
                setFeed([]);
            }
        })();
        return () => { cancelled = true; };
    }, [enqueueSnackbar, closeSnackbar]);

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h1">Bonjour{user?.email ? `, ${user.email}` : ''}.</Typography>
                <Typography variant="body2">
                    Parcourez votre catalogue, suivez vos commandes, ou réassortissez vos essentiels.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h5" sx={{ mb: 2 }}>Commencer</Typography>
                        <Stack spacing={1.5}>
                            <Button component={RouterLink} to="/catalogue" variant="contained" size="large" fullWidth>
                                Parcourir le catalogue
                            </Button>
                            <Button component={RouterLink} to="/orders" variant="outlined" size="large" fullWidth>
                                Mes commandes
                            </Button>
                            <Button component={RouterLink} to="/cart" variant="outlined" size="large" fullWidth>
                                Voir mon panier
                            </Button>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h5" sx={{ mb: 2 }}>Commandes récentes</Typography>
                        {orders === null ? (
                            <Stack spacing={1.5}>
                                {[1, 2, 3].map((i) => <Skeleton key={i} height={40} variant="rectangular" />)}
                            </Stack>
                        ) : orders.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <Typography variant="body2">Aucune commande pour l'instant.</Typography>
                                <Button component={RouterLink} to="/catalogue" sx={{ mt: 2 }}>Parcourir le catalogue</Button>
                            </Box>
                        ) : (
                            <Stack divider={<Divider />}>
                                {orders.map((o) => (
                                    <Box key={o.id} sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                component={RouterLink}
                                                to={`/orders/${o.id}`}
                                                sx={{ color: b2bColors.primary, textDecoration: 'none', fontWeight: 600 }}
                                            >
                                                {o.order_number}
                                            </Typography>
                                            <Typography variant="body2">{formatDate(o.order_date)} · {o.line_count} ligne(s)</Typography>
                                        </Box>
                                        <Chip size="small" label={orderStatusLabel(o.status)} />
                                        <Box className="b2b-num" sx={{ fontWeight: 600, minWidth: 90, textAlign: 'right' }}>
                                            {formatPrice(o.total)}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Promo feed — hidden entirely when empty (design doc UX states) */}
            {feed && feed.length > 0 && (
                <Paper sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <Typography variant="h5">Nouveautés Amabec cette semaine</Typography>
                        <Chip size="small" label="Nouveau" color="secondary" />
                    </Stack>
                    <Grid container spacing={2}>
                        {feed.map((item) => (
                            <Grid item key={item.product_id} xs={12} sm={6} md={4}>
                                <Box sx={{ p: 2, border: `1px solid ${b2bColors.border}`, borderRadius: 1, height: '100%' }}>
                                    <Typography variant="body2" sx={{ fontSize: 11, color: b2bColors.textMuted, letterSpacing: '0.04em' }}>
                                        {item.sku}
                                    </Typography>
                                    <Typography sx={{ fontWeight: 600, mb: 1 }}>{item.name}</Typography>
                                    <Typography className="b2b-num" sx={{ fontSize: 18, fontWeight: 700, color: b2bColors.primary }}>
                                        {formatPrice(item.unit_price)}
                                    </Typography>
                                    {item.social_proof && (
                                        <Typography variant="body2" sx={{ mt: 1, fontSize: 12 }}>
                                            {item.social_proof.customer_count} revendeurs en ont commandé cette semaine
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            )}
        </Stack>
    );
}
